import React, { useState } from 'react';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import PublishIcon from '@material-ui/icons/Publish';
import EditIcon from '@material-ui/icons/Edit';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import CloseIcon from '@material-ui/icons/Close';
import type { ImageListType } from 'react-images-uploading';
import Select from 'react-select';
import type { ValueType } from 'react-select';
import styles from '../furnishingContainer.module.scss';
import portfolioStyles from './portfolio.module.scss';
import type { PortfolioProject, PortfolioRoom, PortfolioReview } from './portfolio.types';
import { ImageUploader } from './ImageUploader';
import Modal from '../../shared/components/core-ui/modal/modal.component';
import { useFurnishingUnitTypes } from '../FurnishingUnitTypesContext';
import type { NumberOfRoomsOption } from '../furnishing.service';
import {
  updatePortfolioRoom,
  updatePortfolioRoomImages,
  updatePortfolioReview,
  deletePortfolioReview
} from '../furnishing.service';
import Button, { ButtonType } from '../../shared/components/core-ui/button/button.component';

interface SelectOption {
  value: string;
  label: string;
}

interface PortfolioListProps {
  projects: PortfolioProject[];
  packages: { value: string; label: string }[];
  onDelete: (id: string) => void;
  onEdit: (id: string, data: Partial<PortfolioProject>, newImage?: File) => Promise<void>;
  onProjectUpdated?: () => void;
  models: { value: string; label: string }[];
}

export const PortfolioList: React.FC<PortfolioListProps> = ({
  projects,
  models,
  packages,
  onDelete,
  onEdit,
  onProjectUpdated
}) => {
  const { unitTypeOptions, loadingUnitTypes, homeTypeOptions, loadingHomeTypes } =
    useFurnishingUnitTypes();
  const [expandedProject, setExpandedProject] = useState<string | null>(null);
  const [activeRoomTab, setActiveRoomTab] = useState<Record<string, string>>({});
  const [currentImageIndex, setCurrentImageIndex] = useState<Record<string, number>>({});
  const [editingProject, setEditingProject] = useState<PortfolioProject | null>(null);
  const [editFormState, setEditFormState] = useState<Partial<PortfolioProject>>({});
  const [editImageList, setEditImageList] = useState<Array<{ dataURL?: string; file?: File }>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editError, setEditError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean;
    projectId: string | null;
    projectName: string;
  }>({ show: false, projectId: null, projectName: '' });
  const [publishingProjectId, setPublishingProjectId] = useState<string | null>(null);
  const [reviewDeleteDialog, setReviewDeleteDialog] = useState<{
    show: boolean;
    reviewId: string | null;
  }>({ show: false, reviewId: null });
  const [isReviewDeleting, setIsReviewDeleting] = useState(false);

  // Room editing state
  const [editingRoom, setEditingRoom] = useState<{
    room: PortfolioRoom;
    projectId: string;
  } | null>(null);
  const [roomEditFormState, setRoomEditFormState] = useState<{ name: string; order: number }>({
    name: '',
    order: 0
  });
  const [roomEditExistingImages, setRoomEditExistingImages] = useState<string[]>([]);
  const [roomEditNewImages, setRoomEditNewImages] = useState<
    Array<{ dataURL?: string; file?: File }>
  >([]);
  const [isRoomSubmitting, setIsRoomSubmitting] = useState(false);
  const [roomEditError, setRoomEditError] = useState('');

  // Review editing state
  const [editingReview, setEditingReview] = useState<{
    review: PortfolioReview;
    projectId: string;
  } | null>(null);
  const [reviewEditFormState, setReviewEditFormState] = useState<{
    name: string;
    description: string;
  }>({ name: '', description: '' });
  const [isReviewSubmitting, setIsReviewSubmitting] = useState(false);
  const [reviewEditError, setReviewEditError] = useState('');
  const [isEditOtherSelected, setIsEditOtherSelected] = useState(false);

  const isProjectPublishable = (project: PortfolioProject): boolean => {
    const hasRequiredData =
      project.projectName &&
      project.projectNameAr &&
      project.description &&
      project.descriptionAr &&
      project.location &&
      project.homeType &&
      project.packageId &&
      project.overviewImage;
    const hasRooms = project.rooms && project.rooms.length > 0;
    return !!(hasRequiredData && hasRooms);
  };

  const handleTogglePublish = async (project: PortfolioProject) => {
    setPublishingProjectId(project.id);

    try {
      await onEdit(project.id, { isPublished: !project.isPublished });
      // Refresh the project list to show updated publish status
      if (onProjectUpdated) {
        onProjectUpdated();
      }
    } finally {
      setPublishingProjectId(null);
    }
  };

  const showDeleteConfirmDialog = (projectId: string, projectName: string) => {
    setConfirmDialog({ show: true, projectId, projectName });
  };

  const hideConfirmDialog = () => {
    setConfirmDialog({ show: false, projectId: null, projectName: '' });
  };

  const handleConfirmDelete = () => {
    if (confirmDialog.projectId) {
      onDelete(confirmDialog.projectId);
    }
    hideConfirmDialog();
  };

  const getPackageLabel = (value: string) => {
    return packages.find((pkg) => pkg.value === value)?.label || value;
  };

  const getModelLabel = (value: string | undefined, otherModel?: string) => {
    if (otherModel) {
      return otherModel;
    }
    return value ? models.find((model) => model.value === value)?.label || value : 'N/A';
  };

  const getHomeTypeLabel = (value: string) => {
    return homeTypeOptions.find((ht) => ht.value === value)?.label || value;
  };

  const handleProjectClick = (projectId: string) => {
    setExpandedProject(expandedProject === projectId ? null : projectId);
  };
  const getNoOfBedroomsLabel = (noOfBedroomsId: number) => {
    return (
      unitTypeOptions.find((ht) => ht.value === String(noOfBedroomsId))?.label || noOfBedroomsId
    );
  };

  const getCurrentRoomTab = (projectId: string) => {
    return activeRoomTab[projectId] || 'Overview';
  };

  const setCurrentRoomTab = (projectId: string, roomName: string) => {
    setActiveRoomTab((prev) => ({ ...prev, [projectId]: roomName }));
    setCurrentImageIndex((prev) => ({ ...prev, [`${projectId}_${roomName}`]: 0 }));
  };

  const getCurrentImage = (projectId: string, roomName: string) => {
    return currentImageIndex[`${projectId}_${roomName}`] || 0;
  };

  const handlePrevImage = (
    e: React.MouseEvent,
    projectId: string,
    roomName: string,
    totalImages: number
  ) => {
    e.stopPropagation();
    const key = `${projectId}_${roomName}`;
    setCurrentImageIndex((prev) => ({
      ...prev,
      [key]: prev[key] === 0 || prev[key] === undefined ? totalImages - 1 : prev[key] - 1
    }));
  };

  const handleNextImage = (
    e: React.MouseEvent,
    projectId: string,
    roomName: string,
    totalImages: number
  ) => {
    e.stopPropagation();
    const key = `${projectId}_${roomName}`;
    setCurrentImageIndex((prev) => ({
      ...prev,
      [key]: ((prev[key] || 0) + 1) % totalImages
    }));
  };

  const handleOpenEditModal = (e: React.MouseEvent, project: PortfolioProject) => {
    e.stopPropagation();
    setEditingProject(project);
    setEditFormState({
      projectName: project.projectName,
      projectNameAr: project.projectNameAr,
      description: project.description,
      descriptionAr: project.descriptionAr,
      location: project.location,
      priceMin: project.priceMin,
      priceMax: project.priceMax,
      homeType: project.homeType,
      packageId: project.packageId,
      numberOfBedrooms: project.numberOfBedrooms,
      timeline: project.timeline,
      year: project.year,
      overviewImage: project.overviewImage,
      isPublished: project?.isPublished,
      modelId: project?.modelId,
      otherModel: project?.otherModel,
      order: project?.order
    });
    setEditImageList([]);
    setEditError('');
    setIsEditOtherSelected(!!project?.otherModel && !project?.modelId);
  };

  const handleCloseEditModal = () => {
    setEditingProject(null);
    setEditFormState({});
    setEditImageList([]);
    setEditError('');
    setIsEditOtherSelected(false);
  };

  const handleEditTextChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = event.target;
    setEditFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditNumberChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    setEditFormState((prev) => ({ ...prev, [name]: Number(value) || 0 }));
  };

  const handleEditSelectChange = (
    name: string,
    selectedOption: ValueType<SelectOption, false>
  ): void => {
    if (selectedOption) {
      setEditFormState((prev) => ({ ...prev, [name]: (selectedOption as SelectOption).label }));
    }
  };

  const handleEditModelUseChange = (
    name: string,
    selectedOption: ValueType<SelectOption, false>
  ): void => {
    if (selectedOption) {
      setEditFormState((prev) => ({ ...prev, [name]: (selectedOption as SelectOption).value }));
    }
  };
  const handleEditPackageUseChange = (
    name: string,
    selectedOption: ValueType<SelectOption, false>
  ): void => {
    if (selectedOption) {
      setEditFormState((prev) => ({ ...prev, [name]: (selectedOption as SelectOption).value }));
    }
  };

  const handleEditNumberSelectChange = (
    name: string,
    selectedOption: ValueType<NumberOfRoomsOption, false>
  ): void => {
    if (selectedOption) {
      setEditFormState((prev) => ({
        ...prev,
        [name]: (selectedOption as NumberOfRoomsOption).value
      }));
    }
  };

  const handleEditImageChange = (imageList: ImageListType) => {
    setEditImageList(imageList as Array<{ dataURL?: string; file?: File }>);
    if (imageList[0]?.dataURL) {
      setEditFormState((prev) => ({ ...prev, overviewImage: imageList[0].dataURL || '' }));
    }
  };

  const handleEditSubmit = async () => {
    if (!editingProject) return;

    const {
      projectName,
      projectNameAr,
      description,
      descriptionAr,
      location,
      homeType,
      packageId,
      timeline,
      modelId,
      otherModel
    } = editFormState;

    if (
      !projectName ||
      !projectNameAr ||
      !description ||
      !descriptionAr ||
      !location ||
      !homeType ||
      !timeline ||
      !packageId ||
      (!modelId && !otherModel)
    ) {
      setEditError('Please fill in all required fields.');
      return;
    }

    if ((editFormState.priceMin || 0) > (editFormState.priceMax || 0)) {
      setEditError('Minimum price cannot be greater than maximum price.');
      return;
    }

    setIsSubmitting(true);
    setEditError('');

    try {
      const newImageFile = editImageList[0]?.file;
      await onEdit(editingProject.id, editFormState, newImageFile);
      handleCloseEditModal();
    } catch (err) {
      setEditError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Room editing handlers
  const handleOpenRoomEditModal = (e: React.MouseEvent, room: PortfolioRoom, projectId: string) => {
    e.stopPropagation();
    setEditingRoom({ room, projectId });
    setRoomEditFormState({ name: room.name, order: room.order || 0 });
    setRoomEditExistingImages([...room.images]);
    setRoomEditNewImages([]);
    setRoomEditError('');
  };

  const handleCloseRoomEditModal = () => {
    setEditingRoom(null);
    setRoomEditFormState({ name: '', order: 0 });
    setRoomEditExistingImages([]);
    setRoomEditNewImages([]);
    setRoomEditError('');
  };

  const handleRoomNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    setRoomEditFormState((prev) => ({ ...prev, name: value }));
  };

  const handleRoomOrderChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    setRoomEditFormState((prev) => ({ ...prev, order: Number(value) || 0 }));
  };

  const handleRemoveExistingImage = (imageUrl: string) => {
    setRoomEditExistingImages((prev) => prev.filter((img) => img !== imageUrl));
  };

  const handleRoomNewImagesChange = (imageList: ImageListType) => {
    setRoomEditNewImages(imageList as Array<{ dataURL?: string; file?: File }>);
  };

  const handleRoomEditSubmit = async () => {
    if (!editingRoom) return;

    if (!roomEditFormState.name.trim()) {
      setRoomEditError('Room name is required.');
      return;
    }

    setIsRoomSubmitting(true);
    setRoomEditError('');

    try {
      // Update room name and order if changed
      if (
        roomEditFormState.name !== editingRoom.room.name ||
        roomEditFormState.order !== (editingRoom.room.order || 0)
      ) {
        const updateResult = await updatePortfolioRoom(editingRoom.room.id, {
          name: roomEditFormState.name,
          order: roomEditFormState.order
        });
        if ('errorCode' in updateResult) {
          setRoomEditError('Failed to update room. Please try again.');
          setIsRoomSubmitting(false);
          return;
        }
      }

      // Update images if there are changes (new images added or existing images removed)
      const newImageFiles = roomEditNewImages
        .filter((img) => img.file)
        .map((img) => img.file as File);

      const hasImageChanges =
        newImageFiles.length > 0 ||
        roomEditExistingImages.length !== editingRoom.room.images.length ||
        !roomEditExistingImages.every((img) => editingRoom.room.images.includes(img));

      if (hasImageChanges) {
        const imageResult = await updatePortfolioRoomImages(
          editingRoom.room.id,
          newImageFiles,
          roomEditExistingImages
        );
        if ('errorCode' in imageResult) {
          setRoomEditError('Failed to update room images. Please try again.');
          setIsRoomSubmitting(false);
          return;
        }
      }

      handleCloseRoomEditModal();
      // Refresh the project list to show updated room data
      if (onProjectUpdated) {
        onProjectUpdated();
      }
    } catch (err) {
      setRoomEditError('An unexpected error occurred. Please try again.');
    } finally {
      setIsRoomSubmitting(false);
    }
  };

  // Review editing handlers
  const handleOpenReviewEditModal = (
    e: React.MouseEvent,
    review: PortfolioReview,
    projectId: string
  ) => {
    e.stopPropagation();
    setEditingReview({ review, projectId });
    setReviewEditFormState({ name: review.name, description: review.description });
    setReviewEditError('');
  };

  const handleCloseReviewEditModal = () => {
    setEditingReview(null);
    setReviewEditFormState({ name: '', description: '' });
    setReviewEditError('');
  };

  const handleReviewFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = event.target;
    setReviewEditFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleReviewEditSubmit = async () => {
    if (!editingReview) return;

    if (!reviewEditFormState.name.trim()) {
      setReviewEditError('Reviewer name is required.');
      return;
    }

    if (!reviewEditFormState.description.trim()) {
      setReviewEditError('Review description is required.');
      return;
    }

    setIsReviewSubmitting(true);
    setReviewEditError('');

    try {
      const updateResult = await updatePortfolioReview(editingReview.review.id, {
        name: reviewEditFormState.name,
        description: reviewEditFormState.description
      });

      if ('errorCode' in updateResult) {
        setReviewEditError('Failed to update review. Please try again.');
        setIsReviewSubmitting(false);
        return;
      }

      handleCloseReviewEditModal();
      if (onProjectUpdated) {
        onProjectUpdated();
      }
    } catch (err) {
      setReviewEditError('An unexpected error occurred. Please try again.');
    } finally {
      setIsReviewSubmitting(false);
    }
  };

  const handleDeleteReview = async (e: React.MouseEvent, reviewId: string) => {
    e.stopPropagation();
    setReviewDeleteDialog({ show: true, reviewId });
  };

  const hideReviewDeleteDialog = () => {
    setReviewDeleteDialog({ show: false, reviewId: null });
  };

  const handleConfirmDeleteReview = async () => {
    if (!reviewDeleteDialog.reviewId) return;

    setIsReviewDeleting(true);
    try {
      const result = await deletePortfolioReview(reviewDeleteDialog.reviewId);
      if ('errorCode' in result) {
        alert('Failed to delete review. Please try again.');
        setIsReviewDeleting(false);
        return;
      }

      hideReviewDeleteDialog();
      if (onProjectUpdated) {
        onProjectUpdated();
      }
    } catch (err) {
      alert('An unexpected error occurred. Please try again.');
    } finally {
      setIsReviewDeleting(false);
    }
  };

  if (projects.length === 0) return null;

  return (
    <div className={portfolioStyles['portfolio-list']}>
      <div className={portfolioStyles['portfolio-list__header']}>
        <h3>Portfolio Projects</h3>
        <span className={styles['furnishing-management__count']}>{projects.length} Projects</span>
      </div>

      <div className={portfolioStyles['portfolio-list__grid']}>
        {projects.map((project) => {
          const isExpanded = expandedProject === project.id;
          const currentRoom = getCurrentRoomTab(project.id);
          const roomData = project.rooms.find((r) => r.name === currentRoom);
          const roomImages = roomData?.images || [];
          const currentImgIdx = getCurrentImage(project.id, currentRoom);
          const modelLabel =
            project.modelId || project.otherModel
              ? getModelLabel(project.modelId, project.otherModel)
              : 'N/A';
          const noOfBedroomsLabel = project.numberOfBedrooms
            ? getNoOfBedroomsLabel(project.numberOfBedrooms)
            : 'N/A';
          return (
            <div
              key={project.id}
              className={`${portfolioStyles['portfolio-card']} ${
                isExpanded ? portfolioStyles['portfolio-card--expanded'] : ''
              }`}
            >
              {/* Compact Header */}
              <div className={portfolioStyles['portfolio-card__header']}>
                <button
                  type="button"
                  className={portfolioStyles['portfolio-card__main-content']}
                  onClick={() => handleProjectClick(project.id)}
                >
                  <div className={portfolioStyles['portfolio-card__thumbnail']}>
                    <img src={project.overviewImage} alt={project.projectName} />
                  </div>

                  <div className={portfolioStyles['portfolio-card__info']}>
                    <h4 className={portfolioStyles['portfolio-card__title']}>
                      {project.projectName || 'Untitled Project'}
                    </h4>
                    <div className={portfolioStyles['portfolio-card__meta']}>
                      <span>{project.location}</span>
                      <span className={portfolioStyles['portfolio-card__separator']}>•</span>
                      <span>{noOfBedroomsLabel}</span>
                      <span className={portfolioStyles['portfolio-card__separator']}>•</span>
                      <span>{getHomeTypeLabel(project.homeType)}</span>
                    </div>
                    <div className={portfolioStyles['portfolio-card__submeta']}>
                      <span
                        className={`${portfolioStyles['portfolio-card__badge']} ${
                          project.isPublished
                            ? portfolioStyles['portfolio-card__badge--success']
                            : portfolioStyles['portfolio-card__badge--muted']
                        }`}
                        aria-label={`Project is ${project.isPublished ? 'published' : 'draft'}`}
                      >
                        {project.isPublished ? 'Published' : 'Draft'}
                      </span>
                      <span className={portfolioStyles['portfolio-card__badge']}>
                        Style: {modelLabel}
                      </span>
                    </div>
                    <div className={portfolioStyles['portfolio-card__price']}>
                      EGP {project.priceMin.toLocaleString()} - {project.priceMax.toLocaleString()}
                    </div>
                  </div>
                </button>

                <div className={portfolioStyles['portfolio-card__actions-compact']}>
                  <button
                    type="button"
                    className={portfolioStyles['portfolio-card__action-btn']}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTogglePublish(project);
                    }}
                    disabled={!project.isPublished && !isProjectPublishable(project)}
                    title={
                      !isProjectPublishable(project) && !project.isPublished
                        ? 'Cannot publish: missing required data or rooms'
                        : project.isPublished
                        ? 'Click to unpublish'
                        : 'Click to publish'
                    }
                    style={{
                      color: project.isPublished ? '#237c58' : '#cbd5e0'
                    }}
                  >
                    {publishingProjectId === project.id ? (
                      <span style={{ fontSize: '0.8rem' }}>...</span>
                    ) : project.isPublished ? (
                      <CheckCircleIcon />
                    ) : (
                      <PublishIcon />
                    )}
                  </button>
                  <button
                    type="button"
                    className={portfolioStyles['portfolio-card__action-btn']}
                    onClick={(e) => handleOpenEditModal(e, project)}
                    aria-label="Edit project"
                  >
                    <EditIcon />
                  </button>
                  <button
                    type="button"
                    className={portfolioStyles['portfolio-card__action-btn']}
                    onClick={(e) => {
                      e.stopPropagation();
                      showDeleteConfirmDialog(project.id, project.projectName);
                    }}
                    aria-label="Delete project"
                    style={{ color: '#e53e3e' }}
                  >
                    <DeleteOutlineIcon />
                  </button>
                  <button
                    type="button"
                    className={`${portfolioStyles['portfolio-card__action-btn']} ${portfolioStyles['portfolio-card__action-btn--view']}`}
                    onClick={() => handleProjectClick(project.id)}
                    aria-label={isExpanded ? 'Collapse details' : 'View details'}
                  >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </button>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className={portfolioStyles['portfolio-card__expanded-content']}>
                  <p className={portfolioStyles['portfolio-card__description']}>
                    {project.description}
                  </p>

                  <div className={portfolioStyles['portfolio-card__details-grid']}>
                    <div className={portfolioStyles['portfolio-card__detail-item']}>
                      <span className={portfolioStyles['portfolio-card__detail-label']}>
                        Package
                      </span>
                      <span>{getPackageLabel(project.packageId)}</span>
                    </div>
                    <div className={portfolioStyles['portfolio-card__detail-item']}>
                      <span className={portfolioStyles['portfolio-card__detail-label']}>
                        Timeline
                      </span>
                      <span>{project.timeline} Weeks</span>
                    </div>
                    <div className={portfolioStyles['portfolio-card__detail-item']}>
                      <span className={portfolioStyles['portfolio-card__detail-label']}>Year</span>
                      <span>{project.year}</span>
                    </div>
                  </div>

                  <div className={portfolioStyles['portfolio-card__rooms']}>
                    <div className={portfolioStyles['portfolio-card__room-tabs']}>
                      {project.rooms.map((room) => (
                        <div
                          key={room.id}
                          className={portfolioStyles['portfolio-card__room-tab-wrapper']}
                        >
                          <button
                            type="button"
                            className={`${portfolioStyles['portfolio-card__room-tab']} ${
                              currentRoom === room.name
                                ? portfolioStyles['portfolio-card__room-tab--active']
                                : ''
                            }`}
                            onClick={() => setCurrentRoomTab(project.id, room.name)}
                          >
                            {room.name}
                          </button>
                          <button
                            type="button"
                            className={portfolioStyles['portfolio-card__room-edit-btn']}
                            onClick={(e) => handleOpenRoomEditModal(e, room, project.id)}
                            aria-label={`Edit ${room.name}`}
                            title={`Edit ${room.name}`}
                          >
                            <EditIcon style={{ fontSize: '0.875rem' }} />
                          </button>
                        </div>
                      ))}
                    </div>

                    {roomImages.length > 0 && (
                      <div className={portfolioStyles['portfolio-card__room-content']}>
                        <div className={portfolioStyles['portfolio-card__room-gallery']}>
                          <img
                            src={roomImages[currentImgIdx]}
                            alt={`${currentRoom} ${currentImgIdx + 1}`}
                          />
                          {roomImages.length > 1 && (
                            <>
                              <button
                                type="button"
                                className={`${portfolioStyles['portfolio-card__gallery-nav']} ${portfolioStyles['portfolio-card__gallery-nav--prev']}`}
                                onClick={(e) =>
                                  handlePrevImage(e, project.id, currentRoom, roomImages.length)
                                }
                              >
                                <ChevronLeftIcon />
                              </button>
                              <button
                                type="button"
                                className={`${portfolioStyles['portfolio-card__gallery-nav']} ${portfolioStyles['portfolio-card__gallery-nav--next']}`}
                                onClick={(e) =>
                                  handleNextImage(e, project.id, currentRoom, roomImages.length)
                                }
                              >
                                <ChevronRightIcon />
                              </button>
                              <div className={portfolioStyles['portfolio-card__gallery-dots']}>
                                {roomImages.map((imgUrl, idx) => (
                                  <span
                                    key={imgUrl}
                                    className={`${portfolioStyles['portfolio-card__gallery-dot']} ${
                                      idx === currentImgIdx
                                        ? portfolioStyles['portfolio-card__gallery-dot--active']
                                        : ''
                                    }`}
                                  />
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Review Section */}
                  {project.review && (
                    <div className={portfolioStyles['portfolio-card__review-section']}>
                      <div className={portfolioStyles['portfolio-card__review-header']}>
                        <h4 className={portfolioStyles['portfolio-card__review-title']}>
                          Client Review
                        </h4>
                        <div className={portfolioStyles['portfolio-card__review-actions']}>
                          <button
                            type="button"
                            className={portfolioStyles['portfolio-card__review-action-btn']}
                            onClick={(e) =>
                              project.review &&
                              handleOpenReviewEditModal(e, project.review, project.id)
                            }
                            aria-label="Edit review"
                            title="Edit review"
                          >
                            <EditIcon style={{ fontSize: '1rem' }} />
                          </button>
                          <button
                            type="button"
                            className={`${portfolioStyles['portfolio-card__review-action-btn']} ${portfolioStyles['portfolio-card__review-action-btn--delete']}`}
                            onClick={(e) =>
                              project.review && handleDeleteReview(e, project.review.id)
                            }
                            aria-label="Delete review"
                            title="Delete review"
                          >
                            <DeleteOutlineIcon style={{ fontSize: '1rem' }} />
                          </button>
                        </div>
                      </div>
                      <div className={portfolioStyles['portfolio-card__review-content']}>
                        <p className={portfolioStyles['portfolio-card__review-text']}>
                          &quot;{project.review.description} &quot;
                        </p>
                        <p className={portfolioStyles['portfolio-card__review-author']}>
                          — {project.review.name}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Edit Modal */}
      <Modal
        config={{
          showModal: !!editingProject,
          fullScreen: false,
          maxWidth: 'md',
          showClose: true
        }}
        eventHandlers={{
          closeModalHandler: handleCloseEditModal
        }}
      >
        <div className={styles['furnishing-management__confirm-dialog']}>
          <h2>Edit Portfolio Project</h2>

          <div className={styles['furnishing-management__form-grid']}>
            <label className={styles['furnishing-management__input']}>
              <span>Project Name *</span>
              <input
                type="text"
                name="projectName"
                placeholder="Enter project name"
                value={editFormState.projectName || ''}
                onChange={handleEditTextChange}
              />
            </label>
            <label className={styles['furnishing-management__input']}>
              <span>Project Name (Arabic) *</span>
              <input
                type="text"
                name="projectNameAr"
                placeholder="أدخل اسم المشروع"
                dir="rtl"
                value={editFormState.projectNameAr || ''}
                onChange={handleEditTextChange}
              />
            </label>
            <label className={styles['furnishing-management__input']}>
              <span>Location *</span>
              <input
                type="text"
                name="location"
                placeholder="e.g., Marina Marassi, North Coast"
                value={editFormState.location || ''}
                onChange={handleEditTextChange}
              />
            </label>
            <label className={styles['furnishing-management__input']}>
              <span>Year</span>
              <input
                type="number"
                name="year"
                placeholder="e.g., 2025"
                min={2000}
                max={2100}
                value={editFormState.year || ''}
                onChange={handleEditNumberChange}
              />
            </label>
          </div>

          <div className={styles['furnishing-management__form-grid']} style={{ marginTop: '1rem' }}>
            <div className={styles['furnishing-management__input']}>
              <span>Home Type *</span>
              <div className={styles['furnishing-management__select']}>
                <Select
                  classNamePrefix="react-select"
                  placeholder="Select home type..."
                  value={
                    homeTypeOptions.find((opt) => opt.label === editFormState.homeType) || null
                  }
                  options={homeTypeOptions}
                  isLoading={loadingHomeTypes}
                  onChange={(option) => handleEditSelectChange('homeType', option)}
                />
              </div>
            </div>
            <div className={styles['furnishing-management__input']}>
              <span>Package Used *</span>
              <div className={styles['furnishing-management__select']}>
                <Select
                  classNamePrefix="react-select"
                  placeholder="Select package..."
                  options={packages}
                  value={packages.find((opt) => opt.value === editFormState.packageId) || null}
                  onChange={(option) => {
                    handleEditPackageUseChange('packageId', option);
                  }}
                />
              </div>
            </div>
            <div className={styles['furnishing-management__input']}>
              <span>Style Used *</span>
              <div className={styles['furnishing-management__select']}>
                <Select
                  classNamePrefix="react-select"
                  placeholder="Select style..."
                  options={[...models, { value: 'other', label: 'Other' }]}
                  value={
                    editFormState.modelId
                      ? models.find((opt) => opt.value === editFormState.modelId) || null
                      : editFormState.otherModel
                      ? { value: 'other', label: 'Other' }
                      : null
                  }
                  onChange={(option) => {
                    if (option?.value === 'other') {
                      setIsEditOtherSelected(true);
                      setEditFormState((prev) => ({ ...prev, modelId: '', otherModel: '' }));
                    } else {
                      setIsEditOtherSelected(false);
                      handleEditModelUseChange('modelId', option);
                      setEditFormState((prev) => ({ ...prev, otherModel: '' }));
                    }
                  }}
                />
              </div>
            </div>
            {isEditOtherSelected && (
              <label className={styles['furnishing-management__input']}>
                <span>Custom Style Name *</span>
                <input
                  type="text"
                  name="otherModel"
                  placeholder="Enter custom style name"
                  value={editFormState.otherModel}
                  onChange={handleEditTextChange}
                />
              </label>
            )}
            <div className={styles['furnishing-management__input']}>
              <span>Number of Bedrooms</span>
              <div className={styles['furnishing-management__select']}>
                <Select
                  classNamePrefix="react-select"
                  placeholder="Select unit type..."
                  options={unitTypeOptions}
                  isLoading={loadingUnitTypes}
                  value={
                    unitTypeOptions.find(
                      (opt) => opt.value === String(editFormState.numberOfBedrooms)
                    ) || null
                  }
                  onChange={(option) => handleEditNumberSelectChange('numberOfBedrooms', option)}
                />
              </div>
            </div>
          </div>

          <div className={styles['furnishing-management__form-grid']} style={{ marginTop: '1rem' }}>
            <label className={styles['furnishing-management__input']}>
              <span>Price Min (EGP)</span>
              <input
                type="number"
                name="priceMin"
                placeholder="e.g., 227000"
                value={editFormState.priceMin || ''}
                onChange={handleEditNumberChange}
              />
            </label>
            <label className={styles['furnishing-management__input']}>
              <span>Price Max (EGP)</span>
              <input
                type="number"
                name="priceMax"
                placeholder="e.g., 230000"
                value={editFormState.priceMax || ''}
                onChange={handleEditNumberChange}
              />
            </label>
            <label className={styles['furnishing-management__input']}>
              <span>Timeline (Weeks)</span>
              <input
                type="number"
                name="timeline"
                placeholder="e.g., 3"
                min={1}
                value={editFormState.timeline || ''}
                onChange={handleEditNumberChange}
              />
            </label>
            <label className={styles['furnishing-management__input']}>
              <span>Order</span>
              <input
                type="number"
                name="order"
                placeholder="e.g., 1"
                min={0}
                value={editFormState.order ?? ''}
                onChange={handleEditNumberChange}
              />
            </label>
          </div>

          <div style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              <div className={styles['furnishing-management__input']} style={{ flex: '1 1 200px' }}>
                <span>Overview Image</span>
                <div style={{ marginBottom: '0.5rem' }}>
                  {editFormState.overviewImage && !editImageList.length && (
                    <img
                      src={editFormState.overviewImage}
                      alt="Current overview"
                      style={{
                        width: '100px',
                        height: '75px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        marginBottom: '0.5rem'
                      }}
                    />
                  )}
                </div>
                <ImageUploader
                  images={editImageList}
                  onImageChange={handleEditImageChange}
                  maxNumber={1}
                  altText="Overview"
                />
                <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                  Upload a new image to replace the current one
                </p>
              </div>
              <label
                className={styles['furnishing-management__input']}
                style={{ flex: '2 1 300px' }}
              >
                <span>Description *</span>
                <textarea
                  name="description"
                  placeholder="Describe the property, style, and key features..."
                  rows={4}
                  value={editFormState.description || ''}
                  onChange={handleEditTextChange}
                />
              </label>
            </div>
            <label className={styles['furnishing-management__input']} style={{ marginTop: '1rem' }}>
              <span>Description (Arabic) *</span>
              <textarea
                name="descriptionAr"
                placeholder="وصف العقار والأسلوب والميزات الرئيسية..."
                dir="rtl"
                rows={3}
                value={editFormState.descriptionAr || ''}
                onChange={handleEditTextChange}
              />
            </label>
          </div>

          {editError && (
            <div
              style={{
                padding: '1rem',
                background: '#ffebee',
                color: '#c62828',
                borderRadius: '8px',
                marginTop: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: '500'
              }}
            >
              <span style={{ fontSize: '1.2rem' }}>⚠️</span> {editError}
            </div>
          )}

          <div className={styles['furnishing-management__confirm-dialog-actions']}>
            <Button type={ButtonType.gray} onClick={handleCloseEditModal} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type={ButtonType.primary} onClick={handleEditSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        config={{
          showModal: confirmDialog.show,
          fullScreen: false,
          maxWidth: 'sm',
          showClose: false
        }}
        eventHandlers={{
          closeModalHandler: hideConfirmDialog
        }}
      >
        <div className={styles['furnishing-management__confirm-dialog']}>
          <h2>Delete Portfolio Project</h2>
          <p>
            Are you sure you want to delete &quot;{confirmDialog.projectName}&quot;? This action
            cannot be undone.
          </p>
          <div className={styles['furnishing-management__confirm-dialog-actions']}>
            <button
              type="button"
              onClick={hideConfirmDialog}
              className={styles['furnishing-management__cancel-btn']}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmDelete}
              className={styles['furnishing-management__delete-confirm-btn']}
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>

      {/* Room Edit Modal */}
      <Modal
        config={{
          showModal: !!editingRoom,
          fullScreen: false,
          maxWidth: 'md',
          showClose: true
        }}
        eventHandlers={{
          closeModalHandler: handleCloseRoomEditModal
        }}
      >
        <div className={styles['furnishing-management__confirm-dialog']}>
          <h2>Edit Room</h2>

          <div className={styles['furnishing-management__form-grid']}>
            <label className={styles['furnishing-management__input']}>
              <span>Room Name *</span>
              <input
                type="text"
                name="name"
                placeholder="Enter room name"
                value={roomEditFormState.name}
                onChange={handleRoomNameChange}
              />
            </label>

            <label className={styles['furnishing-management__input']}>
              <span>Room Order</span>
              <input
                type="number"
                name="order"
                placeholder="Enter display order"
                value={roomEditFormState.order}
                onChange={handleRoomOrderChange}
                min="0"
              />
            </label>
          </div>

          <div style={{ marginTop: '1.5rem' }}>
            <span style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 500 }}>
              Current Images
            </span>
            {roomEditExistingImages.length > 0 ? (
              <div className={portfolioStyles['portfolio-room-edit__existing-images']}>
                {roomEditExistingImages.map((imageUrl) => (
                  <div
                    key={imageUrl}
                    className={portfolioStyles['portfolio-room-edit__image-item']}
                  >
                    <img src={imageUrl} alt="Room" />
                    <button
                      type="button"
                      className={portfolioStyles['portfolio-room-edit__remove-btn']}
                      onClick={() => handleRemoveExistingImage(imageUrl)}
                      aria-label="Remove image"
                    >
                      <CloseIcon style={{ fontSize: '1rem' }} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#888', fontSize: '0.9rem' }}>No images currently uploaded</p>
            )}
          </div>

          <div style={{ marginTop: '1.5rem' }}>
            <span style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 500 }}>
              Add New Images
            </span>
            <ImageUploader
              images={roomEditNewImages}
              onImageChange={handleRoomNewImagesChange}
              maxNumber={10}
              altText="Room"
              multiple
            />
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
              Upload new images to add to this room
            </p>
          </div>

          {roomEditError && (
            <div
              style={{
                padding: '1rem',
                background: '#ffebee',
                color: '#c62828',
                borderRadius: '8px',
                marginTop: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: '500'
              }}
            >
              <span style={{ fontSize: '1.2rem' }}>⚠️</span> {roomEditError}
            </div>
          )}

          <div className={styles['furnishing-management__confirm-dialog-actions']}>
            <Button
              type={ButtonType.gray}
              onClick={handleCloseRoomEditModal}
              disabled={isRoomSubmitting}
            >
              Cancel
            </Button>
            <Button
              type={ButtonType.primary}
              onClick={handleRoomEditSubmit}
              disabled={isRoomSubmitting}
            >
              {isRoomSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Review Delete Confirmation Modal */}
      <Modal
        config={{
          showModal: reviewDeleteDialog.show,
          fullScreen: false,
          maxWidth: 'sm',
          showClose: false
        }}
        eventHandlers={{
          closeModalHandler: hideReviewDeleteDialog
        }}
      >
        <div className={styles['furnishing-management__confirm-dialog']}>
          <h2>Delete Review</h2>
          <p>Are you sure you want to delete this review? This action cannot be undone.</p>
          <div className={styles['furnishing-management__confirm-dialog-actions']}>
            <button
              type="button"
              onClick={hideReviewDeleteDialog}
              className={styles['furnishing-management__cancel-btn']}
              disabled={isReviewDeleting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmDeleteReview}
              className={styles['furnishing-management__delete-confirm-btn']}
              disabled={isReviewDeleting}
            >
              {isReviewDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Review Edit Modal */}
      <Modal
        config={{
          showModal: !!editingReview,
          fullScreen: false,
          maxWidth: 'md',
          showClose: true
        }}
        eventHandlers={{
          closeModalHandler: handleCloseReviewEditModal
        }}
      >
        <div className={styles['furnishing-management__confirm-dialog']}>
          <h2>Edit Review</h2>

          <div className={styles['furnishing-management__form-grid']}>
            <label className={styles['furnishing-management__input']}>
              <span>Reviewer Name *</span>
              <input
                type="text"
                name="name"
                placeholder="Enter reviewer name"
                value={reviewEditFormState.name}
                onChange={handleReviewFieldChange}
              />
            </label>
          </div>

          <div style={{ marginTop: '1rem' }}>
            <label className={styles['furnishing-management__input']}>
              <span>Review *</span>
              <textarea
                name="description"
                placeholder="Enter the review text..."
                rows={6}
                value={reviewEditFormState.description}
                onChange={handleReviewFieldChange}
              />
            </label>
          </div>

          {reviewEditError && (
            <div
              style={{
                padding: '1rem',
                background: '#ffebee',
                color: '#c62828',
                borderRadius: '8px',
                marginTop: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: '500'
              }}
            >
              <span style={{ fontSize: '1.2rem' }}>⚠️</span> {reviewEditError}
            </div>
          )}

          <div className={styles['furnishing-management__confirm-dialog-actions']}>
            <Button
              type={ButtonType.gray}
              onClick={handleCloseReviewEditModal}
              disabled={isReviewSubmitting}
            >
              Cancel
            </Button>
            <Button
              type={ButtonType.primary}
              onClick={handleReviewEditSubmit}
              disabled={isReviewSubmitting}
            >
              {isReviewSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PortfolioList;
